/* ============================================================
   OPORA AUTOSAVE — Build-10
   ============================================================

   Layer 1 (always-on): localStorage autosave every 5s.
     Key: opora_draft_{toolId}
     Value: { inputs: {...}, file_hints: [...], updated_at: ISO }

   Layer 2 (signed-in only): D1 autosave every 30s.
     Endpoint: PUT /api/drafts/:tool_id
     Single draft per (user_id, tool_id) — latest wins.

   On page load:
     1. Check localStorage. If > 1 min old AND non-empty inputs → show
        "You have a saved draft. Resume or start fresh." (no hydrate yet).
     2. If signed in AND localStorage empty AND D1 has draft → same banner.
     3. Resume hydrates; start fresh clears local + D1. ?artifact=ID skips.

   On save (tool's main Save button success):
     → call window.OPORA_AUTOSAVE.clear() to wipe localStorage + DELETE /api/drafts.

   On sign-out: call window.OPORA_AUTOSAVE.clearAll() to wipe all tools.

   Public API:
     initAutosave({ toolId, getInputs, setInputs, restoreBannerContainer })
     window.OPORA_AUTOSAVE.clear()       — called by save button success
     window.OPORA_AUTOSAVE.clearAll()    — called on sign-out
     window.OPORA_AUTOSAVE.flush()       — force D1 sync now (optional)

   Constraints:
     - File inputs (<input type="file">) are NEVER serialized.
       Their name attributes are captured as file_hints for re-attach reminders.
     - D1 sync failures are silent — never block UI.
     - Banner never covers below-the-fold content on mobile.
     - No autosave for anonymous users to D1.
     - Idempotent: serializing then deserializing form state returns same values.
   ============================================================ */

(function (global) {
  'use strict';

  var API_BASE = (typeof window !== 'undefined' && window.OPORA_WORKER_URL) ? window.OPORA_WORKER_URL : '';

  // ─── Constants ──────────────────────────────────────────────
  var LS_KEY_PREFIX   = 'opora_draft_';
  var LS_INTERVAL_MS  = 5000;   // 5s localStorage cadence
  var D1_INTERVAL_MS  = 30000;  // 30s D1 cadence
  var MIN_AGE_MS      = 60000;  // draft must be > 1 min old to show banner

  // ─── Global registry ────────────────────────────────────────
  // Allows multiple tools to coexist (shouldn't happen but safe)
  var _instances = {};

  // ─── Auth helpers ────────────────────────────────────────────
  function getAuthToken() {
    // Clerk puts the session JWT in __session cookie and also exposes it
    // via window.Clerk.session.getToken(). We try both paths.
    try {
      if (global.Clerk && global.Clerk.session) {
        // Synchronous best-effort: use the cached token if available.
        // The full async path is handled in the 30s D1 flush.
        var cachedToken = global.Clerk.session.__unstable__toJSON &&
                          global.Clerk.session.__unstable__toJSON().lastActiveToken;
        if (cachedToken) return cachedToken;
      }
    } catch (e) {}
    // Fallback: check OporaGate (already has the token if logged in)
    try {
      if (global.OporaGate && global.OporaGate.isSignedIn && global.OporaGate.isSignedIn()) {
        var authEl = document.querySelector('[data-clerk-token]');
        if (authEl) return authEl.getAttribute('data-clerk-token');
      }
    } catch (e) {}
    return null;
  }

  async function getAuthTokenAsync() {
    try {
      if (global.Clerk && global.Clerk.session) {
        var token = await global.Clerk.session.getToken();
        if (token) return token;
      }
    } catch (e) {}
    return getAuthToken();
  }

  function isSignedIn() {
    try {
      if (global.Clerk && global.Clerk.user) return true;
      if (global.OporaGate && global.OporaGate.isSignedIn) return global.OporaGate.isSignedIn();
    } catch (e) {}
    return false;
  }

  // ─── localStorage helpers ────────────────────────────────────
  function lsKey(toolId) { return LS_KEY_PREFIX + toolId; }

  function lsGet(toolId) {
    try {
      var raw = localStorage.getItem(lsKey(toolId));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) { return null; }
  }

  function lsSet(toolId, inputs, fileHints) {
    try {
      localStorage.setItem(lsKey(toolId), JSON.stringify({
        inputs: inputs,
        file_hints: fileHints || [],
        updated_at: new Date().toISOString()
      }));
    } catch (e) {}
  }

  function lsClear(toolId) {
    try { localStorage.removeItem(lsKey(toolId)); } catch (e) {}
  }

  function lsClearAll() {
    try {
      var toRemove = [];
      for (var i = 0; i < localStorage.length; i++) {
        var k = localStorage.key(i);
        if (k && k.indexOf(LS_KEY_PREFIX) === 0) toRemove.push(k);
      }
      toRemove.forEach(function(k) { localStorage.removeItem(k); });
    } catch (e) {}
  }

  // ─── Input serialization ────────────────────────────────────
  /**
   * Serialize all form inputs (except type=file) to a plain object.
   * Returns { inputs: {...}, fileHints: [...] }
   */
  function serializeForm(formEl) {
    var inputs = {};
    var fileHints = [];
    if (!formEl) return { inputs: inputs, fileHints: fileHints };

    var elements = formEl.querySelectorAll('input, select, textarea');
    elements.forEach(function (el) {
      var name = el.name || el.id || el.getAttribute('data-field');
      if (!name) return;

      // Skip file inputs — capture name for reminder
      if (el.tagName === 'INPUT' && el.type === 'file') {
        fileHints.push(name);
        return;
      }

      // Skip submit, reset, button
      if (el.tagName === 'INPUT' && (el.type === 'submit' || el.type === 'reset' || el.type === 'button')) {
        return;
      }

      // Checkboxes — store boolean
      if (el.tagName === 'INPUT' && el.type === 'checkbox') {
        inputs[name] = el.checked;
        return;
      }

      // Radio buttons — only store the checked one
      if (el.tagName === 'INPUT' && el.type === 'radio') {
        if (el.checked) inputs[name] = el.value;
        return;
      }

      // Range (slider)
      if (el.tagName === 'INPUT' && el.type === 'range') {
        inputs[name] = el.value;
        return;
      }

      // Everything else (text, number, email, select, textarea)
      inputs[name] = el.value;
    });

    return { inputs: inputs, fileHints: fileHints };
  }

  /**
   * Deserialize saved inputs back into the form.
   * Inverse of serializeForm — idempotent.
   * Skips file inputs (as expected).
   */
  function hydrateForm(formEl, inputs) {
    if (!formEl || !inputs || typeof inputs !== 'object') return;

    var elements = formEl.querySelectorAll('input, select, textarea');
    elements.forEach(function (el) {
      var name = el.name || el.id || el.getAttribute('data-field');
      if (!name || !(name in inputs)) return;

      // Skip file inputs — cannot set programmatically
      if (el.tagName === 'INPUT' && el.type === 'file') return;
      // Skip submit/reset/button
      if (el.tagName === 'INPUT' && (el.type === 'submit' || el.type === 'reset' || el.type === 'button')) return;

      var value = inputs[name];

      // Checkboxes
      if (el.tagName === 'INPUT' && el.type === 'checkbox') {
        el.checked = Boolean(value);
        return;
      }

      // Radio buttons — check the one matching the saved value
      if (el.tagName === 'INPUT' && el.type === 'radio') {
        el.checked = (el.value === String(value));
        return;
      }

      // Everything else
      if (value !== null && value !== undefined) {
        el.value = String(value);
        // Fire input + change so any reactive listeners (sliders, counters) update
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // ─── D1 sync ────────────────────────────────────────────────

  async function d1Put(toolId, inputs, fileHints) {
    try {
      var token = await getAuthTokenAsync();
      if (!token) return; // anonymous — skip D1
      await fetch(API_BASE + '/api/drafts/' + toolId, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({ inputs: inputs, file_hints: fileHints || [] })
      });
      // Best-effort — ignore response
    } catch (e) {}
  }

  async function d1Get(toolId) {
    try {
      var token = await getAuthTokenAsync();
      if (!token) return null;
      var resp = await fetch(API_BASE + '/api/drafts/' + toolId, {
        method: 'GET',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (!resp.ok) return null;
      var data = await resp.json();
      return data.exists ? data : null;
    } catch (e) { return null; }
  }

  async function d1Delete(toolId) {
    try {
      var token = await getAuthTokenAsync();
      if (!token) return;
      fetch(API_BASE + '/api/drafts/' + toolId, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      // fire-and-forget
    } catch (e) {}
  }

  // ─── Relative time helper ────────────────────────────────────
  function relativeTime(isoString) {
    try {
      var ms = Date.now() - new Date(isoString).getTime();
      var sec = Math.floor(ms / 1000);
      if (sec < 60) return 'just now';
      var min = Math.floor(sec / 60);
      if (min < 60) return min + ' min ago';
      var hr = Math.floor(min / 60);
      if (hr < 24) return hr + 'h ago';
      return Math.floor(hr / 24) + 'd ago';
    } catch (e) { return 'a moment ago'; }
  }

  // ─── Restoration banner ──────────────────────────────────────
  /**
   * Mounts the shared "saved draft" banner (Resume / start fresh).
   * Does NOT restore until the user clicks Resume.
   * @param {Element} container  — where to prepend the banner
   * @param {Array}   fileHints  — optional file field names to re-attach
   * @param {function} onResume  — restore draft into the form
   * @param {function} onFresh   — clear draft and leave form empty
   */
  function mountBanner(container, fileHints, onResume, onFresh) {
    var existing = document.getElementById('opora-autosave-banner');
    if (existing) existing.remove();

    var banner = document.createElement('div');
    banner.id = 'opora-autosave-banner';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    banner.className = 'ot-draft-banner opora-autosave-banner';

    var fileHintsHTML = '';
    if (fileHints && fileHints.length > 0) {
      var hintNames = fileHints.map(function (h) {
        return h.replace(/-/g, ' ').replace(/_/g, ' ');
      }).join(', ');
      fileHintsHTML =
        '<p class="ot-draft-banner__hint">' +
        'Re-attach your file' + (fileHints.length > 1 ? 's' : '') + ': <strong>' + escapeHTML(hintNames) + '</strong>' +
        '</p>';
    }

    banner.innerHTML =
      '<p class="ot-draft-banner__msg">' +
        'You have a saved draft. ' +
        '<button type="button" class="ot-draft-banner__resume" id="opora-draft-resume">Resume</button>' +
        ' or ' +
        '<button type="button" class="ot-draft-banner__fresh" id="opora-draft-fresh">start fresh</button>.' +
      '</p>' +
      fileHintsHTML;

    if (!document.getElementById('opora-autosave-styles')) {
      var style = document.createElement('style');
      style.id = 'opora-autosave-styles';
      style.textContent = [
        '.ot-draft-banner,.opora-autosave-banner,.sow-resume-banner{',
          'background:#F2F5F7;',
          'border:1px solid #D9DEE3;',
          'border-radius:10px;',
          'padding:12px 16px;',
          'margin-bottom:16px;',
          'max-width:100%;',
          'box-sizing:border-box;',
          'font-family:inherit;',
          'font-size:13.5px;',
          'line-height:1.45;',
          'color:#4A5560;',
          'animation:opora-autosave-fadein 0.2s ease;',
        '}',
        '@keyframes opora-autosave-fadein{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}',
        '.ot-draft-banner__msg,.sow-resume-banner p{margin:0;}',
        '.ot-draft-banner__hint{',
          'margin:8px 0 0;',
          'font-size:12.5px;',
          'color:#6B7280;',
        '}',
        '.ot-draft-banner__resume,.ot-draft-banner__fresh,',
        '.sow-link-btn{',
          'background:none;',
          'border:none;',
          'padding:0;',
          'font:inherit;',
          'cursor:pointer;',
          'text-decoration:underline;',
          'color:#0F7A70;',
        '}',
        '.ot-draft-banner__resume,.sow-link-btn:not(.sow-link-btn--muted){',
          'font-weight:700;',
          'color:#0F7A70;',
        '}',
        '.ot-draft-banner__fresh,.sow-link-btn--muted{',
          'font-weight:500;',
          'color:#4A5560;',
        '}',
        '@media(max-width:640px){',
          '.ot-draft-banner,.opora-autosave-banner,.sow-resume-banner{margin-bottom:12px;padding:10px 12px;}',
        '}'
      ].join('');
      document.head.appendChild(style);
    }

    banner.querySelector('#opora-draft-resume').addEventListener('click', function () {
      banner.remove();
      if (typeof onResume === 'function') onResume();
    });
    banner.querySelector('#opora-draft-fresh').addEventListener('click', function () {
      banner.remove();
      if (typeof onFresh === 'function') onFresh();
    });

    if (container) {
      container.insertBefore(banner, container.firstChild);
    } else {
      var main = document.querySelector('main') || document.querySelector('.ot-content') || document.body;
      main.insertBefore(banner, main.firstChild);
    }

    return banner;
  }

  // ─── escapeHTML (local, no dep on opora-tools) ──────────────
  function escapeHTML(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ─── inputsAreEmpty ─────────────────────────────────────────
  function inputsAreEmpty(inputs) {
    if (!inputs || typeof inputs !== 'object') return true;
    var vals = Object.values(inputs);
    if (!vals.length) return true;
    return vals.every(function (v) {
      if (v === null || v === undefined || v === false) return true;
      if (typeof v === 'string' && v.trim() === '') return true;
      return false;
    });
  }

  // ─── Core: initAutosave ──────────────────────────────────────
  /**
   * Initialize autosave for a tool page.
   *
   * @param {object} opts
   * @param {string}   opts.toolId               — e.g. 'bid-drafter'
   * @param {function} opts.getInputs             — () => { inputs, fileHints } or just inputs obj
   * @param {function} opts.setInputs             — (inputs) => void
   * @param {Element}  [opts.restoreBannerContainer] — where to mount banner (default: auto)
   */
  function initAutosave(opts) {
    var toolId = opts.toolId;
    var getInputsFn = opts.getInputs;
    var setInputsFn = opts.setInputs;
    var bannerContainer = opts.restoreBannerContainer || null;

    if (!toolId) { console.warn('[autosave] toolId is required'); return; }
    if (typeof getInputsFn !== 'function') { console.warn('[autosave] getInputs must be a function'); return; }
    if (typeof setInputsFn !== 'function') { console.warn('[autosave] setInputs must be a function'); return; }

    // If ?artifact=ID is in URL, skip restoration entirely
    var urlParams = new URLSearchParams(global.location.search);
    var artifactParam = urlParams.get('artifact');
    var skipRestoration = Boolean(artifactParam);

    var lsInterval = null;
    var d1Interval = null;

    // ── 5s localStorage save ──────────────────────────────────
    function doLocalSave() {
      try {
        var result = getInputsFn();
        var inputs, fileHints;
        if (result && typeof result === 'object' && 'inputs' in result) {
          inputs = result.inputs;
          fileHints = result.fileHints || result.file_hints || [];
        } else {
          inputs = result;
          fileHints = [];
        }
        if (!inputsAreEmpty(inputs)) {
          lsSet(toolId, inputs, fileHints);
        }
      } catch (e) {}
    }

    // ── 30s D1 save ───────────────────────────────────────────
    async function doD1Save() {
      if (!isSignedIn()) return;
      try {
        var result = getInputsFn();
        var inputs, fileHints;
        if (result && typeof result === 'object' && 'inputs' in result) {
          inputs = result.inputs;
          fileHints = result.fileHints || result.file_hints || [];
        } else {
          inputs = result;
          fileHints = [];
        }
        if (!inputsAreEmpty(inputs)) {
          await d1Put(toolId, inputs, fileHints);
        }
      } catch (e) {}
    }

    // ── Start intervals ───────────────────────────────────────
    lsInterval = setInterval(doLocalSave, LS_INTERVAL_MS);
    d1Interval = setInterval(doD1Save, D1_INTERVAL_MS);

    // ── On-load: offer Resume / start fresh when a draft exists ─
    // Do not hydrate until Resume (matches SOW + Edge draft banner).
    if (!skipRestoration) {
      (async function offerDraft() {
        var lsDraft = lsGet(toolId);
        if (lsDraft && !inputsAreEmpty(lsDraft.inputs)) {
          var ageMs = Date.now() - new Date(lsDraft.updated_at).getTime();
          if (ageMs > MIN_AGE_MS) {
            mountBanner(
              bannerContainer,
              lsDraft.file_hints || [],
              function onResume() {
                try { setInputsFn(lsDraft.inputs); } catch (e) {}
              },
              function onFresh() {
                lsClear(toolId);
                d1Delete(toolId);
                try { setInputsFn({}); } catch (e) {}
              }
            );
            return;
          }
        }

        if (isSignedIn()) {
          var d1Draft = await d1Get(toolId);
          if (d1Draft && !inputsAreEmpty(d1Draft.inputs)) {
            mountBanner(
              bannerContainer,
              d1Draft.file_hints || [],
              function onResume() {
                try { setInputsFn(d1Draft.inputs); } catch (e) {}
                try { lsSet(toolId, d1Draft.inputs, d1Draft.file_hints || []); } catch (e2) {}
              },
              function onFresh() {
                d1Delete(toolId);
                lsClear(toolId);
                try { setInputsFn({}); } catch (e) {}
              }
            );
          }
        }
      })();
    }

    // ── Store instance ────────────────────────────────────────
    var instance = {
      toolId: toolId,
      /** Clear both localStorage and D1 draft for this tool (call on save success) */
      clear: function () {
        lsClear(toolId);
        d1Delete(toolId);
        // Remove banner if visible
        var b = document.getElementById('opora-autosave-banner');
        if (b) b.remove();
      },
      /** Force immediate D1 flush (optional) */
      flush: function () {
        doD1Save();
      },
      /** Destroy timers (for SPA teardown) */
      destroy: function () {
        if (lsInterval) clearInterval(lsInterval);
        if (d1Interval) clearInterval(d1Interval);
        delete _instances[toolId];
      }
    };

    _instances[toolId] = instance;

    // Expose on global for save-button callbacks
    // window.OPORA_AUTOSAVE points to the LAST initialized instance.
    // For multi-tool pages, use _instances[toolId] directly.
    global.OPORA_AUTOSAVE = instance;
    global.OPORA_AUTOSAVE_INSTANCES = _instances;

    return instance;
  }

  // ─── clearAll — call on sign-out ────────────────────────────
  function clearAll() {
    lsClearAll();
    Object.keys(_instances).forEach(function (tid) {
      try { d1Delete(tid); } catch (e) {}
    });
  }

  // ─── Public API ──────────────────────────────────────────────
  global.OporaAutosave = {
    init:     initAutosave,
    clearAll: clearAll,
    serializeForm:  serializeForm,
    hydrateForm:    hydrateForm,
  };

  // Convenience: wire sign-out to clearAll
  document.addEventListener('DOMContentLoaded', function () {
    // Watch for Clerk sign-out event
    try {
      if (global.Clerk) {
        global.Clerk.addListener(function (state) {
          if (state && !state.user) { clearAll(); }
        });
      }
    } catch (e) {}

    // Watch for opora sign-out button (fallback)
    document.addEventListener('click', function (e) {
      var target = e.target && e.target.closest('[data-opora-signout]');
      if (target) clearAll();
    });
  });

})(window);
